import type {
  CollaborationInboxItem,
  CollaborationPartner,
  CollaborationPartnerProfile,
  CollaborationPrincipalContactChangeRequest,
  CollaborationShareInvitation
} from './collaborationControlPlaneShared';

export type CollaborationExperienceTab = 'overview' | 'partners' | 'outbound' | 'inbound' | 'settings';

export const resolveCollaborationExperienceTab = (value: string | null): CollaborationExperienceTab => {
  if (value === 'partners' || value === 'outbound' || value === 'inbound' || value === 'settings') {
    return value;
  }
  return 'overview';
};

export interface CollaborationOverviewAttention {
  id: string;
  title: string;
  detail: string;
  tab: CollaborationExperienceTab;
  partnerId?: string;
  tone: 'warning' | 'danger';
}

export interface CollaborationOverviewModel {
  totalSupplierCount: number;
  activeSupplierCount: number;
  setupSupplierCount: number;
  openSentCount: number;
  receivedReviewCount: number;
  newSupplierActivityCount: number;
  newSentActivityCount: number;
  newReceivedActivityCount: number;
  attention: CollaborationOverviewAttention[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

const ageInDays = (value: string, now: Date): number | null => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return Math.max(0, Math.floor((now.getTime() - parsed.getTime()) / DAY_MS));
};

export const buildCollaborationOverviewModel = ({
  partners,
  profiles,
  shares,
  inbox,
  principalChangeRequests,
  newSentActivityCount = 0,
  newReceivedActivityCount = 0,
  now = new Date()
}: {
  partners: CollaborationPartner[];
  profiles: CollaborationPartnerProfile[];
  shares: CollaborationShareInvitation[];
  inbox: CollaborationInboxItem[];
  principalChangeRequests: CollaborationPrincipalContactChangeRequest[];
  newSentActivityCount?: number;
  newReceivedActivityCount?: number;
  now?: Date;
}): CollaborationOverviewModel => {
  const activeSupplierCount = partners.filter((partner) => partner.status === 'active').length;
  const setupPartners = partners.filter((partner) =>
    ['draft', 'awaiting_supplier_registration', 'awaiting_activation', 'declined'].includes(partner.status)
  );
  const attention: CollaborationOverviewAttention[] = [];

  for (const partner of setupPartners) {
    attention.push({
      id: `setup-${partner.partnerId}`,
      title: `${partner.displayName} needs a setup step`,
      detail:
        partner.status === 'awaiting_activation'
          ? 'Microsoft registration and acceptance are complete. Review and activate the supplier.'
          : partner.status === 'declined'
            ? 'The previous administrator invitation was declined. Review the supplier and issue a replacement when ready.'
            : 'Open the Supplier Profile to see the next valid setup action.',
      tab: 'partners',
      partnerId: partner.partnerId,
      tone: partner.status === 'declined' ? 'danger' : 'warning'
    });
  }

  for (const request of principalChangeRequests.filter((entry) => entry.status === 'pending')) {
    attention.push({
      id: `principal-${request.principalContactChangeRequestId}`,
      title: `${request.partnerDisplayName || 'A supplier'} requested a principal-contact change`,
      detail: 'Review the proposed governance contact in the Supplier Profile.',
      tab: 'partners',
      partnerId: request.partnerId,
      tone: 'warning'
    });
  }

  for (const profile of profiles) {
    const pendingInvite = profile.adminInvites
      .filter((invite) => invite.status === 'pending')
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt))[0];
    if (!pendingInvite) continue;
    const expiresAt = new Date(pendingInvite.expiresAt);
    const daysOld = ageInDays(pendingInvite.createdAt, now);
    const daysUntilExpiry = Number.isNaN(expiresAt.getTime())
      ? null
      : Math.ceil((expiresAt.getTime() - now.getTime()) / DAY_MS);
    if (daysUntilExpiry !== null && daysUntilExpiry <= 7) {
      attention.push({
        id: `invite-expiry-${pendingInvite.adminInviteId}`,
        title: `${profile.partner.displayName}'s administrator invitation ${daysUntilExpiry < 0 ? 'has expired' : 'expires soon'}`,
        detail:
          daysUntilExpiry < 0
            ? 'Open the Supplier Profile and issue a replacement link.'
            : `The link expires in ${daysUntilExpiry} ${daysUntilExpiry === 1 ? 'day' : 'days'}. Check progress or issue a replacement.`,
        tab: 'partners',
        partnerId: profile.partner.partnerId,
        tone: daysUntilExpiry < 0 ? 'danger' : 'warning'
      });
    } else if (daysOld !== null && daysOld >= 7) {
      attention.push({
        id: `invite-stale-${pendingInvite.adminInviteId}`,
        title: `${profile.partner.displayName}'s administrator invitation is still waiting`,
        detail: `Issued ${daysOld} days ago. Check with the supplier or issue a replacement link.`,
        tab: 'partners',
        partnerId: profile.partner.partnerId,
        tone: 'warning'
      });
    }
  }

  for (const partner of partners.filter((entry) => entry.status === 'active')) {
    const latestShare = shares
      .filter((share) => share.partnerId === partner.partnerId)
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0];
    const daysSinceExchange = latestShare ? ageInDays(latestShare.updatedAt, now) : null;
    if (!latestShare || (daysSinceExchange !== null && daysSinceExchange >= 90)) {
      attention.push({
        id: `dormant-${partner.partnerId}`,
        title: `${partner.displayName} has no recent collaboration`,
        detail: latestShare
          ? `The last exchange changed ${daysSinceExchange} days ago.`
          : 'No supplier work has been exchanged yet.',
        tab: 'partners',
        partnerId: partner.partnerId,
        tone: 'warning'
      });
    }
  }

  return {
    totalSupplierCount: partners.length,
    activeSupplierCount,
    setupSupplierCount: setupPartners.length,
    openSentCount: shares.filter((share) => share.status === 'pending' || share.status === 'accepted').length,
    receivedReviewCount: inbox.filter((item) => !['rejected', 'archived'].includes(item.status)).length,
    newSupplierActivityCount: newSentActivityCount + newReceivedActivityCount,
    newSentActivityCount,
    newReceivedActivityCount,
    attention
  };
};
